import React from 'react';
import { cn } from '../../lib/utils';
import type { TableColumn } from './Table';

export interface MobileTableCardProps<T> {
  /** Row data to display */
  data: T;
  /** Column definitions (same as Table component) */
  columns: TableColumn<T>[];
  /** Render function for actions */
  renderActions?: (row: T) => React.ReactNode;
  /** Callback when card is clicked */
  onRowClick?: (row: T) => void;
  /** Custom className */
  className?: string;
}

/**
 * Mobile card view for table rows
 * - Displays data as stacked key-value pairs
 * - Touch-friendly with min 44px tap target
 * - Active state feedback for better UX
 * - Actions displayed at bottom if provided
 *
 * @example
 * ```tsx
 * <MobileTableCard
 *   data={user}
 *   columns={columns}
 *   onRowClick={handleClick}
 *   renderActions={(row) => <Button>Edit</Button>}
 * />
 * ```
 */
export function MobileTableCard<T>({
  data,
  columns,
  renderActions,
  onRowClick,
  className,
}: MobileTableCardProps<T>) {
  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-4',
        'min-h-[44px] transition-colors',
        onRowClick && 'cursor-pointer active:bg-gray-50',
        className
      )}
      onClick={() => onRowClick?.(data)}
    >
      {/* Stacked key-value pairs */}
      <div className="space-y-3">
        {columns.map((column) => {
          const value = data[column.key];
          const displayValue = column.render
            ? column.render(value, data)
            : String(value);

          return (
            <div key={String(column.key)} className="flex flex-col">
              <span className="text-xs font-medium text-gray-500 mb-1">
                {column.header}
              </span>
              <span className="text-sm text-gray-900">{displayValue}</span>
            </div>
          );
        })}
      </div>

      {/* Actions at bottom */}
      {renderActions && (
        <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
          {renderActions(data)}
        </div>
      )}
    </div>
  );
}

MobileTableCard.displayName = 'MobileTableCard';
